import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, formatError, num } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Kpi, Panel, Table, Td, NavyBtn } from "@/pages/portals/ui";

export const MILESTONES = ["Water comfort", "Floating", "Breath control", "Roll to back", "Exit the water", "Life-jacket use", "Water competency"];
export const ENROLL = ["referred", "enrolled", "active", "completed", "withdrawn"];

function Row({ c, refresh }) {
  const [f, setF] = useState({ sessions_completed: c.sessions_completed, enrollment_status: c.enrollment_status, milestones: c.milestones || [], completion_date: c.completion_date || "" });
  const save = async () => {
    try { await api.patch(`/portal/provider/children/${c.id}`, { ...f, sessions_completed: Number(f.sessions_completed) }); toast.success(`${c.ref} updated`); refresh(); } catch (e) { toast.error(formatError(e)); }
  };
  const tog = (m) => setF({ ...f, milestones: f.milestones.includes(m) ? f.milestones.filter((x) => x !== m) : [...f.milestones, m] });
  return (
    <tr className="border-b border-slate-50" data-testid={`provider-child-${c.ref}`}>
      <Td className="font-bold">{c.ref}</Td><Td>{c.age_range}</Td><Td>{c.program}</Td>
      <Td><div className="flex items-center gap-1"><Input className="w-16 h-8" type="number" value={f.sessions_completed} onChange={(e) => setF({ ...f, sessions_completed: e.target.value })} data-testid={`provider-sessions-${c.ref}`} /><span className="text-xs">/ {c.sessions_authorized}</span></div></Td>
      <Td><Select value={f.enrollment_status} onValueChange={(v) => setF({ ...f, enrollment_status: v })}><SelectTrigger className="h-8 w-28" data-testid={`provider-status-${c.ref}`}><SelectValue /></SelectTrigger><SelectContent>{ENROLL.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Td>
      <Td><div className="flex flex-wrap gap-1 max-w-xs">{MILESTONES.map((m) => <button key={m} onClick={() => tog(m)} className={`text-[10px] rounded-full px-2 py-0.5 border ${f.milestones.includes(m) ? "bg-green-500 text-white border-green-500" : "border-slate-200"}`}>{m}</button>)}</div></Td>
      <Td><NavyBtn onClick={save} data-testid={`provider-save-${c.ref}`}>Save</NavyBtn></Td>
    </tr>
  );
}

export default function ProviderPortal() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["portal", "provider"], queryFn: () => api.get("/portal/provider").then((r) => r.data) });
  if (!data) return <p>Loading…</p>;
  const s = data.stats;
  return (
    <div className="space-y-6" data-testid="provider-portal">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Children referred / enrolled" value={num(s.enrolled)} testid="provider-kpi-enrolled" />
        <Kpi label="Sessions authorized" value={num(s.authorized)} testid="provider-kpi-authorized" />
        <Kpi label="Sessions completed" value={num(s.completed)} testid="provider-kpi-completed" />
        <Kpi label="Program completions" value={num(s.graduated)} testid="provider-kpi-graduated" />
      </div>
      <div className="flex gap-3 rounded-2xl bg-sky-50 border border-sky-200 p-4 text-sm text-[#03045E]"><ShieldCheck className="w-5 h-5 shrink-0" />Children are identified by reference code only. Personal details are held by Swim for Rakim administrators and are never shown publicly.</div>
      <Panel title="Scholarship swimmers" testid="provider-children">
        <Table cols={["Ref", "Age", "Program", "Sessions", "Status", "Milestones", ""]} empty="No scholarship swimmers have been referred to you yet." rows={data.children.map((c) => <Row key={c.id} c={c} refresh={() => qc.invalidateQueries({ queryKey: ["portal", "provider"] })} />)} />
      </Panel>
    </div>
  );
}
